/**
 * ═══════════════════════════════════════════════════════════════════════════
 * TWO-WAY SYNC TYPE DEFINITIONS
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * Type definitions for the mailbox two-way sync system between Dashboard and
 * Smartlead (or other sequencers like Instantly in the future).
 */

// ═══════════════════════════════════════════════════════════════════════════
// SEQUENCER TYPES
// ═══════════════════════════════════════════════════════════════════════════

export type SequencerType = 'smartlead' | 'instantly'

export type SyncStatus = 'synced' | 'pending' | 'conflict' | 'failed' | 'never_synced'

export type SyncDirection = 'dashboard_to_sequencer' | 'sequencer_to_dashboard' | 'bidirectional'

// ═══════════════════════════════════════════════════════════════════════════
// MAILBOX SEQUENCER CONNECTION
// ═══════════════════════════════════════════════════════════════════════════

export interface MailboxSequencerConnection {
  id: string
  mailbox_id: string
  sequencer_platform: SequencerType // Maps to DB column
  sequencer_mailbox_id: string | null // Their internal ID
  is_active: boolean
  sync_status: SyncStatus
  last_synced_at: string | null // DB column name
  last_synced_from: string | null // 'dashboard' or 'sequencer'
  
  // Sequencer-specific metrics (from Smartlead/Instantly)
  warmup_status: string | null
  warmup_reputation: number | null
  warmup_sent_total: number | null
  warmup_spam_total: number | null
  current_daily_sent: number | null
  bounce_rate: number | null
  spam_score: number | null
  connection_health: string | null
  
  // Connection status
  smtp_success: boolean | null
  imap_success: boolean | null
  last_connection_check: string | null
  connection_error: string | null
  
  // Error tracking
  sync_error: string | null
  sync_attempt_count: number
  
  // Metadata
  sequencer_metadata: Record<string, any> | null
  created_at: string
  updated_at: string
}

// ═══════════════════════════════════════════════════════════════════════════
// ACTIVITY DISCREPANCY
// ═══════════════════════════════════════════════════════════════════════════

export type DiscrepancySeverity = 'low' | 'medium' | 'high' | 'critical'

export interface MailboxActivityDiscrepancy {
  id: string
  mailbox_id: string
  sequencer_type: SequencerType
  date_detected: string
  
  // Counts
  dashboard_sent_count: number
  sequencer_sent_count: number
  difference: number
  difference_percentage: number
  severity: DiscrepancySeverity
  
  // Status
  is_resolved: boolean
  resolution_notes: string | null
  resolved_at: string | null
  resolved_by: string | null
  
  // Alert tracking
  alert_sent: boolean
  alert_sent_at: string | null
  
  created_at: string
  updated_at: string
}

// ═══════════════════════════════════════════════════════════════════════════
// SYNC ALERTS
// ═══════════════════════════════════════════════════════════════════════════

export type AlertType = 'new_mailbox_imported' | 'sync_conflict' | 'activity_discrepancy' | 'sync_failure' | 'connection_lost'

export type AlertSeverity = 'info' | 'warning' | 'error' | 'critical'

export type AlertStatus = 'new' | 'acknowledged' | 'resolved' | 'dismissed'

export interface SyncAlert {
  id: string
  alert_type: AlertType
  severity: AlertSeverity
  status: AlertStatus
  
  // Related entities
  mailbox_id: string | null
  sequencer_type: SequencerType | null
  
  // Alert details
  title: string
  message: string
  details: Record<string, any> | null
  
  // Slack integration
  slack_sent: boolean
  slack_sent_at: string | null
  slack_message_ts: string | null
  slack_channel: string | null
  
  // Resolution
  acknowledged_at: string | null
  acknowledged_by: string | null
  resolved_at: string | null
  resolved_by: string | null
  resolution_notes: string | null
  
  created_at: string
  updated_at: string
}

// ═══════════════════════════════════════════════════════════════════════════
// SYNC OPERATION TYPES
// ═══════════════════════════════════════════════════════════════════════════

export interface SyncConflict {
  mailbox_id: string
  email: string
  field: string
  dashboard_value: any
  sequencer_value: any
  dashboard_updated_at: string | null
  sequencer_updated_at: string | null
  resolution: 'dashboard_wins' | 'sequencer_wins' | 'manual'
}

export interface TwoWaySyncResult {
  success: boolean
  message: string
  
  // Sync stats
  mailboxes_synced: number
  conflicts_detected: number
  conflicts_resolved: number
  new_mailboxes_imported: number
  discrepancies_found: number
  alerts_created: number
  
  // Details
  conflicts: SyncConflict[]
  imported_mailboxes: string[]
  discrepancies: MailboxActivityDiscrepancy[]
  errors: string[]
  
  // Timing
  started_at: string
  completed_at: string
  duration_ms: number
}

export interface DashboardToSequencerPushResult {
  success: boolean
  mailbox_email: string
  sequencer_type: SequencerType
  fields_updated: string[]
  conflicts_detected: string[]
  error: string | null
}

export interface SequencerToDashboardPullResult {
  success: boolean
  mailbox_email: string
  sequencer_type: SequencerType
  fields_updated: string[]
  conflict_logged: boolean
  error: string | null
}

// ═══════════════════════════════════════════════════════════════════════════
// SYNC PROGRESS
// ═══════════════════════════════════════════════════════════════════════════

export type TwoWaySyncPhase = 
  | 'initializing'
  | 'fetching_dashboard'
  | 'fetching_sequencer'
  | 'comparing'
  | 'pushing_to_sequencer'
  | 'pulling_from_sequencer'
  | 'detecting_conflicts'
  | 'resolving_conflicts'
  | 'checking_discrepancies'
  | 'creating_alerts'
  | 'finalizing'
  | 'completed'

export interface TwoWaySyncProgress {
  phase: TwoWaySyncPhase
  processed: number
  total: number
  percentage: number
  current_mailbox: string | null
  message: string
  conflicts_found: number
  alerts_created: number
}

export type TwoWaySyncProgressCallback = (progress: TwoWaySyncProgress) => void

// ═══════════════════════════════════════════════════════════════════════════
// SMARTLEAD UPDATE PAYLOADS
// ═══════════════════════════════════════════════════════════════════════════

export interface SmartleadUpdatePayload {
  max_email_per_day?: number
  custom_tracking_url?: string
  bcc?: string
  signature?: string
  client_id?: number | null
  time_to_wait_in_mins?: number | null
}

export interface SmartleadWarmupUpdatePayload {
  warmup_enabled: boolean
  total_warmup_per_day?: number
  daily_rampup?: number
  reply_rate_percentage?: number
  warmup_key_id?: string
}

// ═══════════════════════════════════════════════════════════════════════════
// FIELD MAPPING
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Maps dashboard fields to Smartlead API fields
 */
export const DASHBOARD_TO_SMARTLEAD_FIELD_MAP: Record<string, string> = {
  'message_per_day': 'max_email_per_day',
  'signature': 'signature',
  'bcc_email': 'bcc',
  'custom_tracking_domain': 'custom_tracking_url',
  'client_id': 'client_id',
  'minimum_time_gap': 'time_to_wait_in_mins',
  'warmup_status': 'warmup_enabled',
  'from_name': 'from_name',
}

/**
 * Maps Smartlead API fields to dashboard fields
 */
export const SMARTLEAD_TO_DASHBOARD_FIELD_MAP: Record<string, string> = {
  'max_email_per_day': 'message_per_day',
  'message_per_day': 'message_per_day',
  'signature': 'signature',
  'bcc_email': 'bcc_email',
  'custom_tracking_domain': 'custom_tracking_domain',
  'custom_tracking_url': 'custom_tracking_domain',
  'client_id': 'client_id',
  'daily_sent_count': 'current_daily_sent',
  'warmup_enabled': 'warmup_status',
  'from_name': 'from_name',
  'time_to_wait_in_mins': 'minimum_time_gap',
}

/**
 * Fields that can be synced from Dashboard to Smartlead
 */
export const SYNCABLE_DASHBOARD_FIELDS = [
  'message_per_day',
  'signature',
  'bcc_email',
  'custom_tracking_domain',
  'client_id',
  'minimum_time_gap',
  'warmup_status',
  'from_name',
] as const

/**
 * Fields that should be pulled from Smartlead (read-only from sequencer)
 */
export const SEQUENCER_SOURCE_FIELDS = [
  'warmup_status',
  'warmup_reputation',
  'current_daily_sent',
  'smtp_success',
  'imap_success',
  'bounce_rate',
  'spam_rate',
] as const

